import { useCallback, useEffect, useState } from 'react';
import {
  Environment,
  getBgClassForColorId,
  getDefaultColorIdForEnv,
  getEnvironment,
  getOverrideColorId,
  clearOverrideColorId,
  notifyBgChange,
  resolveActiveColorId,
  setOverrideColorId,
  subscribeBgChange,
} from '../utils/envBackground';

/**
 * useEnvBackground — estado reactivo del tinte de fondo full-width.
 *
 * - `env`: ambiente detectado por hostname (puro).
 * - `activeColorId` / `bgClass`: color efectivo (override manual o default por
 *   ambiente).
 * - `defaultColorId`: default del ambiente (para saber si hay override).
 * - `hasOverride`: true si el usuario eligió un color manual.
 * - `setColor(id)`: fija override manual (persiste + notifica).
 * - `resetToDefault()`: borra override → vuelve al default del ambiente.
 *
 * Usa el pub/sub de envBackground para que cualquier consumidor (App.tsx y el
 * Debug Drawer) se mantengan sincronizados sin acoplar contexts.
 */
export function useEnvBackground() {
  const env: Environment = getEnvironment();
  const [activeColorId, setActiveColorId] = useState<string>(() => resolveActiveColorId(env));

  const refresh = useCallback(() => {
    setActiveColorId(resolveActiveColorId(env));
  }, [env]);

  useEffect(() => subscribeBgChange(refresh), [refresh]);

  const setColor = useCallback(
    (id: string) => {
      setOverrideColorId(id);
      notifyBgChange();
    },
    []
  );

  const resetToDefault = useCallback(() => {
    clearOverrideColorId();
    notifyBgChange();
  }, []);

  return {
    env,
    activeColorId,
    bgClass: getBgClassForColorId(activeColorId),
    defaultColorId: getDefaultColorIdForEnv(env),
    hasOverride: getOverrideColorId() !== null,
    setColor,
    resetToDefault,
  };
}
